<script>
import RawData from "@/components/widgets/raw-data.vue";

// Mock data for subsidiary ledger transactions table
const mockTransactionsData = [
    {
        id: 1,
        date: "2025-03-15",
        referenceJev: "010000-03-0015",
        particulars: "Mobilization Fee Advance (15% of contract)",
        debit: 1500000.00,
        credit: 0.00,
        balance: 1500000.00
    },
    {
        id: 2,
        date: "2025-05-30",
        referenceJev: "010000-05-0040",
        particulars: "Deduction from 1st Progress Billing (20% of advance)",
        debit: 0.00,
        credit: 300000.00,
        balance: 1200000.00
    },
    {
        id: 3,
        date: "2025-07-15",
        referenceJev: "010000-07-0055",
        particulars: "Deduction from 2nd Progress Billing (20% of advance)",
        debit: 0.00,
        credit: 300000.00,
        balance: 900000.00
    },
    {
        id: 4,
        date: "2025-09-20",
        referenceJev: "010000-09-0012",
        particulars: "Advance for Steel Materials",
        debit: 500000.00,
        credit: 0.00,
        balance: 1400000.00
    },
    {
        id: 5,
        date: "2025-10-30",
        referenceJev: "010000-10-0070",
        particulars: "Deduction from 3rd Progress Billing (60% of original advance)",
        debit: 0.00,
        credit: 900000.00,
        balance: 500000.00
    }
];

// Table fields configuration
const tableFields = [
    {
        key: "date",
        label: "Date",
        sortable: true,
        thClass: "text-center",
        tdClass: "text-center",
        formatter: (value) => {
            return new Date(value).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        }
    },
    {
        key: "referenceJev",
        label: "Reference JEV",
        sortable: true,
        thClass: "text-center",
        tdClass: "text-center"
    },
    {
        key: "particulars",
        label: "Particulars",
        sortable: false,
        thClass: "text-center",
        tdClass: "text-left"
    },
    {
        key: "debit",
        label: "Debit",
        sortable: true,
        thClass: "text-center",
        tdClass: "text-right",
        formatter: (value) => {
            return value > 0 ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'PHP'
            }).format(value) : '-';
        }
    },
    {
        key: "credit",
        label: "Credit",
        sortable: true,
        thClass: "text-center",
        tdClass: "text-right",
        formatter: (value) => {
            return value > 0 ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'PHP'
            }).format(value) : '-';
        }
    },
    {
        key: "balance",
        label: "Balance",
        sortable: true,
        thClass: "text-center",
        tdClass: "text-right",
        formatter: (value) => {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'PHP'
            }).format(value);
        }
    }
];

export default {
    name: "SubsidiaryLedgerTransactionsModal",
    components: {
        RawData
    },
    props: {
        subsidiaryLedger: {
            type: Object,
            default: () => null
        },
        modalTitle: {
            type: String,
            default: "Subsidiary Ledger Transactions"
        }
    },
    data() {
        return {
            transactions: mockTransactionsData,
            fields: tableFields,
            loading: false,
            currentPage: 1,
            perPage: 10,
            filter: null,
            dateFrom: new Date(new Date().getFullYear(), 0, 1).toISOString().substr(0, 10), // Start of current year
            dateTo: new Date().toISOString().substr(0, 10) // End of current day
        };
    },
    computed: {
        beginningBalance() {
            return 0.00;
        },
        totalRows() {
            return this.filteredTransactions.length;
        },
        totalDebit() {
            return this.filteredTransactions.reduce((total, transaction) => {
                return total + (transaction.debit || 0);
            }, 0);
        },
        totalCredit() {
            return this.filteredTransactions.reduce((total, transaction) => {
                return total + (transaction.credit || 0);
            }, 0);
        },
        currentBalance() {
            return this.totalDebit - this.totalCredit;
        },
        filteredTransactions() {
            let filtered = [...this.transactions];

            // Filter by search text
            if (this.filter) {
                const filterLower = this.filter.toLowerCase();
                filtered = filtered.filter(transaction => {
                    return (
                        transaction.referenceJev.toLowerCase().includes(filterLower) ||
                        transaction.particulars.toLowerCase().includes(filterLower) ||
                        this.formatDate(transaction.date).toLowerCase().includes(filterLower)
                    );
                });
            }

            // Filter by date range
            if (this.dateFrom || this.dateTo) {
                filtered = filtered.filter(transaction => {
                    const transactionDate = new Date(transaction.date);
                    const fromDate = this.dateFrom ? new Date(this.dateFrom) : null;
                    const toDate = this.dateTo ? new Date(this.dateTo) : null;

                    if (fromDate && toDate) {
                        return transactionDate >= fromDate && transactionDate <= toDate;
                    } else if (fromDate) {
                        return transactionDate >= fromDate;
                    } else if (toDate) {
                        return transactionDate <= toDate;
                    }
                    return true;
                });
            }

            return filtered;
        },
        pageOptions() {
            return [
                { value: 5, text: '5' },
                { value: 10, text: '10' },
                { value: 25, text: '25' },
                { value: 50, text: '50' },
                { value: 100, text: '100' }
            ];
        }
    },
    methods: {
        formatCurrency(value) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'PHP'
            }).format(value);
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        },
        formatDateTime(date) {
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        },
        printTransaction(transaction) {
            this.$emit('print-transaction', transaction);
        },
        closeModal() {
            this.$bvModal.hide('subsidiary-ledger-transactions-modal');
        },
    }
};
</script>

<template>
    <b-modal
        id="subsidiary-ledger-transactions-modal"
        :title="modalTitle"
        size="xxl"
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
    >
        <div class="subsidiary-ledger-transactions">
            <!-- Header Info -->
            <div class="card shadow-none border-info mb-3">
                <div class="card-header bg-info bg-soft text-white">
                    <div class="row align-items-center">
                        <div class="col">
                            <h6 class="card-title mb-0 text-white">
                                {{ subsidiaryLedger ? subsidiaryLedger.code : 'N/A' }}
                            </h6>
                            <small v-if="subsidiaryLedger">
                                {{ subsidiaryLedger.clientDto ? subsidiaryLedger.clientDto.name : subsidiaryLedger.client || 'N/A' }} |
                                Account: {{ subsidiaryLedger.accountNumber || 'N/A' }}
                            </small>
                        </div>
                        <div class="col-auto">
                            <!-- <button 
                                type="button" 
                                class="btn btn-light btn-sm"
                                @click="exportToExcel"
                                :disabled="!transactions.length"
                            >
                                <i class="bx bx-download me-1"></i>
                                Export
                            </button> -->
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="row mb-3">
                <div class="col-md-3">
                    <div class="card border-success shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-success mb-1">Total Debit</h6>
                                    <h5 class="mb-0">{{ formatCurrency(totalDebit) }}</h5>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-success">
                                        <i class="bx bx-plus-circle font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3">
                    <div class="card border-danger shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-danger mb-1">Total Credit</h6>
                                    <h5 class="mb-0">{{ formatCurrency(totalCredit) }}</h5>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-danger">
                                        <i class="bx bx-minus-circle font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3">
                    <div class="card border-info shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-info mb-1">Current Balance</h6>
                                    <h5 class="mb-0">{{ formatCurrency(currentBalance) }}</h5>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-info">
                                        <i class="bx bx-wallet font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3">
                    <div class="card border-warning shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-warning mb-1">Transactions</h6>
                                    <h5 class="mb-0">{{ transactions.length }}</h5>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-warning">
                                        <i class="bx bx-list-ul font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filter and Controls -->
            <div class="row mb-3">
                <div class="col-md-8">
                    <div class="d-flex gap-2 align-items-end flex-wrap">
                        <b-form-input
                            v-model="filter"
                            type="text"
                            placeholder="Search transactions..."
                            style="width: 200px;"
                        ></b-form-input>
                        <b-form-select
                            class="form-select"
                            v-model="perPage"
                            :options="pageOptions"
                            style="width: 80px;"
                        ></b-form-select>
                        
                        
                        <div class="d-flex gap-2 align-items-center">
                            <label class="form-label mb-0 text-nowrap">Date From:</label>
                            <b-form-input
                                v-model="dateFrom"
                                type="date"
                                style="width: 150px;"
                            ></b-form-input>
                        </div>
                        
                        <div class="d-flex gap-2 align-items-center">
                            <label class="form-label mb-0 text-nowrap">Date To:</label>
                            <b-form-input
                                v-model="dateTo"
                                type="date"
                                style="width: 150px;"
                            ></b-form-input>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 text-end">
                    <small class="text-muted">
                        Showing {{ Math.min((currentPage - 1) * perPage + 1, filteredTransactions.length) }} to 
                        {{ Math.min(currentPage * perPage, filteredTransactions.length) }} of {{ filteredTransactions.length }} transactions
                        <span v-if="filteredTransactions.length !== totalRows" class="text-info">
                            (filtered from {{ transactions.length }} total)
                        </span>
                    </small>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2 mb-0">Loading transactions...</p>
            </div>

            <!-- No Data State -->
            <div v-else-if="!transactions.length" class="text-center py-5">
                <div class="mb-3">
                    <i class="bx bx-folder-open font-size-48 text-muted"></i>
                </div>
                <h4 class="text-muted">No Transactions Found</h4>
                <p class="text-muted">This subsidiary ledger has no transactions yet.</p>
            </div>

            <!-- Transactions Table -->
            <div v-else>
                <div class="table-responsive">
                    <b-table
                        :items="filteredTransactions"
                        :fields="fields"
                        :current-page="currentPage"
                        :per-page="perPage"
                        :busy="loading"
                        responsive
                        show-empty
                        empty-text="No transactions found"
                        class="table-bordered table-hover mb-0"
                        thead-class="table-light"
                        tbody-tr-class="align-middle"
                        hover
                    >
                        <template #cell(date)="data">
                            <div class="position-relative">
                                <RawData :data="data.item" v-if="$store.state.decrypt.isAdmin()" />
                                {{ formatDateTime(data.item.date) }}
                            </div>
                        </template>

                        <template #cell(referenceJEV)="data">
                            <span class="text-primary fw-medium">{{ data.value }}</span>
                        </template>

                        <template #cell(particulars)="data">
                            <div class="text-truncate" :title="data.value">
                                {{ data.value }}
                            </div>
                        </template>

                        <template #cell(debit)="data">
                            <span v-if="data.value" class="text-success fw-bold">
                                {{ data.value }}
                            </span>
                        </template>

                        <template #cell(credit)="data">
                            <span v-if="data.value" class="text-danger fw-bold">
                                {{ data.value }}
                            </span>
                        </template>

                        <template #cell(balance)="data">
                            <span class="text-info fw-bold">
                                {{ data.value }}
                            </span>
                        </template>

                        <template #cell(actions)="data">
                            <div class="d-flex justify-content-center gap-1">
                                <button
                                    type="button"
                                    class="btn btn-link p-0 action-btn"
                                    @click="editTransaction(data.item)"
                                    title="Edit Transaction"
                                >
                                    <i class="bx bx-edit font-size-18 text-primary"></i>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-link p-0 action-btn"
                                    @click="deleteTransaction(data.item)"
                                    title="Delete Transaction"
                                >
                                    <i class="bx bx-trash font-size-18 text-danger"></i>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-link p-0 action-btn"
                                    @click="printTransaction(data.item)"
                                    title="Print Transaction"
                                >
                                    <i class="bx bx-printer font-size-18 text-purple"></i>
                                </button>
                            </div>
                        </template>
                    </b-table>
                </div>

                <!-- Pagination -->
                <div class="row mt-3" v-if="totalRows > perPage">
                    <div class="col">
                        <div class="dataTables_paginate paging_simple_numbers float-end">
                            <ul class="pagination pagination-rounded mb-0">
                                <b-pagination
                                    v-model="currentPage"
                                    :total-rows="totalRows"
                                    :per-page="perPage"
                                    aria-controls="transactions-table"
                                ></b-pagination>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Footer -->
        <template #modal-footer>
            <div class="d-flex justify-content-end gap-2">
                <b-button
                    variant="secondary"
                    @click="$bvModal.hide('subsidiary-ledger-transactions-modal')"
                >
                    Close
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<style scoped>
.action-btn {
    transition: all 0.2s ease;
}

.action-btn:hover {
    transform: scale(1.1);
}

.table-hover tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.05);
}

.avatar-sm {
    width: 2.5rem;
    height: 2.5rem;
}

.avatar-title {
    width: 100%;
    height: 100%;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>